import type { CSSProperties, HTMLAttributes } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import css from './table.module.scss';

type TableContainerProps = HTMLAttributes<HTMLDivElement> & {
  // Opt-in horizontal scroll. When the combined cell `minWidth`s exceed
  // the viewport the container scrolls sideways and the rows keep their
  // natural width (`min-width: max-content`) instead of squashing. Off by
  // default so existing tables are unchanged.
  scrollable?: boolean;
};

export const TableContainer = ({
  scrollable = false,
  className,
  children,
  ...restProps
}: TableContainerProps) => (
  <div
    className={joinCss(
      css.container,
      scrollable && css.isScrollable,
      className
    )}
    {...restProps}
  >
    {children}
  </div>
);

type TableRowProps = HTMLAttributes<HTMLDivElement> & {
  header?: boolean;
  // Opt-in shorter row. The 56px header / 52px body defaults suit roomy
  // tables; data-dense screens (operations) pay for that in visible rows.
  // Opt-in rather than a new default so no existing table shifts.
  compact?: boolean;
  // Opt-in: body rows grow with wrapping content (role pills, etc.)
  // instead of the fixed 52px height overflowing into the header.
  autoHeight?: boolean;
  // Shared horizontal floor (e.g. `'1640px'`) for a scrollable table —
  // the sum of the columns' bases. Applied as `--row-min-width` so every
  // row keeps the same width and the columns line up; the container
  // scrolls when the viewport is narrower.
  minWidth?: string;
};

export const TableRow = ({
  header = false,
  compact = false,
  autoHeight = false,
  minWidth,
  className,
  children,
  style,
  ...restProps
}: TableRowProps) => (
  <div
    className={joinCss(
      css.row,
      header && css.isHeader,
      compact && css.isCompact,
      autoHeight && css.isAutoHeight,
      className
    )}
    style={
      minWidth
        ? ({ ...style, '--row-min-width': minWidth } as CSSProperties)
        : style
    }
    {...restProps}
  >
    {children}
  </div>
);

type TableCellAlign = 'left' | 'center' | 'right';

type TableCellProps = HTMLAttributes<HTMLDivElement> & {
  width?: string;
  // The column's preferred size, used as `flex-basis`. The column then
  // distributes identically across the header and every body row (no
  // per-row drift). It is NOT a per-cell `min-width` — content truncates
  // (`.cell` has `min-width: 0`) so a long value never inflates the row.
  // The row's overall floor is one shared `--row-min-width` on the row.
  basis?: string;
  // Relative growth weight. Columns with the same `flexGrow` share leftover
  // space equally; a `flexGrow: 5` column eats 5× the share. MUST match
  // between the header cell and the body cell of the same column.
  flexGrow?: number;
  align?: TableCellAlign;
};

export const TableCell = ({
  width,
  basis,
  flexGrow,
  align = 'left',
  className,
  children,
  style,
  ...restProps
}: TableCellProps) => {
  // A fixed `width` pins the cell. Otherwise build ONE coherent flex value
  // — `flex: <grow> 1 <basis>` — so the header and every row compute the
  // same column widths. No per-cell `min-width`: `.cell { min-width: 0 }`
  // lets content truncate, and the row carries the shared scroll floor.
  const flexStyle = width
    ? { flex: `0 0 ${width}`, maxWidth: width }
    : { flex: `${flexGrow ?? 1} 1 ${basis ?? '0'}` };

  return (
    <div
      className={joinCss(
        css.cell,
        align === 'center' && css.cellAlignCenter,
        align === 'right' && css.cellAlignRight,
        className
      )}
      style={{ ...style, ...flexStyle }}
      {...restProps}
    >
      {children}
    </div>
  );
};

type TableFooterProps = HTMLAttributes<HTMLDivElement>;

export const TableFooter = ({
  className,
  children,
  ...restProps
}: TableFooterProps) => (
  <div className={joinCss(css.footer, className)} {...restProps}>
    {children}
  </div>
);
